import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, Subject, takeUntil, throwError } from 'rxjs';
import { saveAs } from 'file-saver';
import { GlobalService } from '@core/services/global/global.service';

@Injectable({
  providedIn: 'root',
})
export class FileuploadService {
  private destroy$ = new Subject<void>();
  constructor(
    protected http: HttpClient,
    private globalService: GlobalService,
  ) {}

  public async exportDatawithQueryStringParameters(
    url: string,
    filename: string,
    payload: any,
  ) {
    this.exportFile(url, filename, payload);
  }

  private getBackEndWebApiToken(): string | null {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      return sessionStorage.getItem('dotnetbearerToken');
    }
    return null;
  }

  private exportFile(url: string, fileName: string, response: any): void {
    const token = this.getBackEndWebApiToken();
    const groupMemberCode = this.globalService.getGroupMemberCode();
    const role = this.globalService.getSelectedRole();
    const payload = {
      ...response,
      groupMemberCode,
      role,
    };

    const headers = new HttpHeaders({
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-APIM-Access': 'true',
    });

    this.http
      .post(url, payload, { headers, responseType: 'blob' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          // Check if the blob is empty
          if (blob.size === 0) {
            const emptyBlob = new Blob(
              [JSON.stringify({ message: 'Empty file received' })],
              { type: 'application/json' },
            );
            saveAs(emptyBlob, 'empty_file_warning.json');
            return;
          }

          // Process JSON blob
          if (blob.type === 'application/json') {
            const fileReader = new FileReader();
            fileReader.onload = () => {
              try {
                const json = JSON.parse(fileReader.result as string);
                if (json?.success && !json.isDataExists) {
                  console.warn('Data not found in the response.');
                }
              } catch (e) {
                let errorMessage = 'An unknown error occurred';
                console.error('Error parsing JSON:', e);
                if (e instanceof Error) {
                  errorMessage = e.message;
                } else if (typeof e === 'string') {
                  errorMessage = e;
                }
                const errorBlob = new Blob(
                  [
                    JSON.stringify({
                      error: 'Invalid JSON received',
                      details: errorMessage,
                    }),
                  ],
                  { type: 'application/json' },
                );
                saveAs(errorBlob, 'error_parsing.json');
              }
            };
            fileReader.readAsText(blob);
          } else {
            // If not JSON, save the file as an .xlsx
            saveAs(blob, `${fileName}.xlsx`);
          }
        },
        error: (error) => {
          console.error('Download error:', error);
          const errorBlob = new Blob(
            [
              JSON.stringify({
                error: 'Download error',
                details: error.message,
              }),
            ],
            { type: 'application/json' },
          );
          saveAs(errorBlob, 'error_response.json');
          return Promise.resolve({ success: true });
        },
      });
  }

  public async exportData(url: string, response: any, filename: string) {
    this.exportFile(url, filename, response); // Pass response (payload) for the post request
  }

  uploadFile(apiUrl: string, formData: FormData): Observable<any> {
    const token = this.getBackEndPythonWebApiToken();
    const headers = new HttpHeaders({
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    });

    return this.http
      .post<any>(apiUrl, formData, { headers })
      .pipe(
        catchError((error) =>
          throwError(() => new Error(`Upload error: ${error.message}`)),
        ),
      );
  }

  private getBackEndPythonWebApiToken(): string | null {
    return sessionStorage.getItem('pythonbearerToken');
  }
}
